import React, { useState, useEffect, } from "react";
import { Typography, Grid2, Paper } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

const schema = z.object({
  fullName: z.string().min(1, 'Full Name is required'),
  icno: z.string()
      .min(1, "IC No is required")
      .regex(/^\d{12}$/, "IC No must be exactly 12 digits"),
  emailAddress: z.string()
      .min(1, "Email Address is required")
      .email("Invalid email format"),
  phoneNo: z.string()
      .min(1, "Phone No is required")
      .regex(/^(\+?60|0)[1-9]\d{7,9}$/, "Invalid Malaysian phone number"),
  username: z.string()
      .min(3, "Username must be at least 3 characters")
      .max(20, "Username must be at most 20 characters")
      .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
  password: z.string()
      .min(8, "Password must be at least 8 characters")
      .max(32, "Password must be at most 32 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/\d/, "Password must contain at least one number")
      .regex(/[\W_]/, "Password must contain at least one special character"),
  confirmPassword: z.string().min(1, 'Confirm Password is required'),
})
.refine((data) => data.password === data.confirmPassword, {
  path: ["confirmPassword"],
  message: "Passwords do not match",
});


export default function UserForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create-user" || "view-user" || "edit-user"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch } = useForm({
    resolver: zodResolver(schema),
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit-user" || pageAction === "view-user") && !id) || (pageAction === "create-user" && id)) {
      onBack()
    }
    if (id) {
      fetchUserMutation.mutate(id)
    }
  },[pageAction])

  const fetchUserMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getUser}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      // console.log(data)
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve user", "error");
    }
  });

  const createUserMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createUser, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getUser'] })
      openSnackbar("Successfully created user", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data?.message, "error");
      else openSnackbar("Failed to create user", "error");
    }
  })

  const updateUserMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateUser}/${id}`, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getUser'] })
      openSnackbar("Successfully updated user", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data?.message, "error");
      else openSnackbar("Failed to update user", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    if(pageAction === "create-user")
      createUserMutation.mutate(data)
    else
      updateUserMutation.mutate(data)
  }

  const onBack = () => {
    navigate('/admin/security?tab=users', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchUserMutation.isPending} isError={fetchUserMutation.isError} errorMessage="Unable to load user" disabled={["create-user"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>

          <Grid2 size={12}>
            <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Personal Details</Typography>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="fullName"
              control={control}
              label="Full Name"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="icno"
              control={control}
              label="IC No"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="emailAddress"
              control={control}
              label="Email Address"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="phoneNo"
              control={control}
              label="Phone No"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>
        
        </Grid2>
      </Paper>

      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>

          <Grid2 size={12}>
            <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Account Details</Typography>
          </Grid2>

          <Grid2 size={12}>
            <ControlledTextField 
              name="username"
              control={control}
              label="Username"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="password"
              control={control}
              label="Password"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="confirmPassword"
              control={control}
              label="Confirm Password"
              disabled={fetchUserMutation.isPending || fetchUserMutation.isError}
              readOnly={["view-user"].includes(pageAction)}
            />
          </Grid2>
        </Grid2>
      </Paper>

      {["create-user", "edit-user"].includes(pageAction) && 
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
            display: "flex",
            justifyContent: 'flex-end',
            gap: "10px"
          }}
        >
          <CustomButton type="submit" disabled={fetchUserMutation.isPending || fetchUserMutation.isError}>Submit</CustomButton>
        </Paper>
      }
      </form>
      </SkeletonWrapper>

      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit user?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createUserMutation.isPending || updateUserMutation.isPending}
      />
    </div>
  );
}